import React, { useState, useEffect } from 'react';
import { ADMIN_API_ENDPOINTS, FRONTEND_URL, SERVER_URL } from '../config/api';

const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null); // 跟踪哪个图片正在上传


  // 获取完整图片URL的辅助函数
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // 如果已经是完整URL,直接返回
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // 如果是相对路径,添加SERVER_URL前缀
    return `${SERVER_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  // 上传图片
  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件', 'error');
      return;
    }

    // 验证文件大小 (30MB)
    if (file.size > 30 * 1024 * 1024) {
      showToast('图片大小不能超过30MB', 'error');
      return;
    }

    // 如果没有活动ID,不能上传图片
    if (!currentEvent.event_id) {
      showToast('请先生成活动ID后再上传图片', 'error');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('eventId', currentEvent.event_id);

    try {
      setIsLoading(true);
      setUploadingImage(index); // 设置当前上传的图片索引
      setUploadingImage(index); // 设置当前上传的图片索引
      const response = await fetch(ADMIN_API_ENDPOINTS.EVENTS.UPLOAD_IMAGE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // 更新图片URL - 创建新数组确保React检测到变化
        const newImages = [...(currentEvent.images || [])];
        newImages[index] = data.imageUrl;

        // 使用函数式更新确保获取最新状态
        setCurrentEvent((prev) => ({
          ...prev,
          images: newImages,
          // 添加时间戳强制重新渲染
          _imageUpdateTimestamp: Date.now(),
        }));

        showToast('图片上传成功', 'success');
      } else {
        showToast(data.message || '上传失败', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('上传失败', 'error');
    } finally {
      setIsLoading(false);
      setUploadingImage(null); // 清除上传状态
      // 清空文件输入,允许重新上传相同文件
      e.target.value = '';
    }
  };

  // 活动模板
  const eventTemplate = {
    event_id: '',
    name: { en: '', zh: '' },
    date: { en: '', zh: '' },
    location: { en: '', zh: '' },
    description: { en: '', zh: '' },
    images: [],
    status: 'published', // 默认已发布，新增活动立即生效
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // 获取活动列表
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_API_ENDPOINTS.EVENTS.LIST, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        showToast('登录已过期，请重新登录', 'error');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched events:', data.events); // 调试日志
        setEvents(data.events || []);
      } else {
        showToast('获取活动列表失败', 'error');
      }
    } catch (error) {
      console.error('获取活动列表失败:', error);
      showToast('获取活动列表失败', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 显示Toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 创建新活动
  const handleCreateEvent = () => {
    setCurrentEvent({ ...eventTemplate });
    setShowEditor(true);
  };

  // 编辑活动
  const handleEditEvent = (event) => {
    setCurrentEvent({ ...event });
    setShowEditor(true);
  };

  // 发布/取消发布活动
  const handleTogglePublish = async (event) => {
    const newStatus = event.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(ADMIN_API_ENDPOINTS.EVENTS.BY_ID(event.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
        body: JSON.stringify({ ...event, status: newStatus }),
      });
      if (response.ok) {
        showToast(
          newStatus === 'published' ? '活动已发布' : '活动已下架',
          'success'
        );
        fetchEvents();
      }
    } catch (error) {
      showToast('操作失败', 'error');
    }
  };

  // 删除活动
  const handleDeleteEvent = (eventId) => {
    setConfirmDialog({
      message: '确定要删除这个活动吗？此操作不可撤销。',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(
            ADMIN_API_ENDPOINTS.EVENTS.BY_ID(eventId),
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  ADMIN_TOKEN_KEY
                )}`,
              },
            }
          );
          if (response.ok) {
            showToast('活动已删除', 'success');
            fetchEvents();
          }
        } catch (error) {
          showToast('删除失败', 'error');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  // 保存活动
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const url = currentEvent.id
        ? ADMIN_API_ENDPOINTS.EVENTS.BY_ID(currentEvent.id)
        : ADMIN_API_ENDPOINTS.EVENTS.LIST;

      const method = currentEvent.id ? 'PUT' : 'POST';

      // 准备发送的数据
      const eventData = { ...currentEvent };

      // 创建新活动时,不发送event_id(后端会自动生成)
      // 更新活动时,将event_id改名为id发送给后端
      if (currentEvent.id) {
        eventData.id = currentEvent.event_id;
        delete eventData.event_id;
      } else {
        // 新活动,删除event_id字段
        delete eventData.event_id;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
        body: JSON.stringify(eventData),
      });

      if (response.ok) {
        showToast(currentEvent.id ? '活动已更新' : '活动已创建', 'success');
        setShowEditor(false);
        fetchEvents();
      } else {
        const data = await response.json();
        showToast(data.message || '保存失败', 'error');
      }
    } catch (error) {
      console.error('保存失败:', error);
      showToast('保存失败', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Toast自动消失
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">活动管理</h1>
        <button
          onClick={handleCreateEvent}
          className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            viewBox="0 0 256 256"
          >
            <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
          </svg>
          创建新活动
        </button>
      </div>

      {/* 活动列表 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        {isLoading && !showEditor ? (
          <div className="p-8 text-center text-gray-400">
            <div className="inline-block">
              <div className="w-8 h-8 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="mt-2">加载中...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            暂无活动,点击上方按钮创建第一个活动
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2d27]">
                  <th className="text-left p-4 text-gray-400 font-medium">
                    活动名称
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    英文名称
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    时间
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    地点
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    状态
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  // 解析JSON字段
                  const parsedName =
                    typeof event.name === 'string'
                      ? JSON.parse(event.name)
                      : event.name;
                  const parsedDate =
                    typeof event.date === 'string'
                      ? JSON.parse(event.date)
                      : event.date;
                  const parsedLocation =
                    typeof event.location === 'string'
                      ? JSON.parse(event.location)
                      : event.location;

                  return (
                    <tr
                      key={event.id}
                      className="border-b border-[#2a2d27] hover:bg-[#262a22] transition-colors"
                    >
                      <td className="p-4 text-white font-medium">
                        {parsedName?.zh || parsedName?.en || '未命名活动'}
                      </td>
                      <td className="p-4 text-gray-400">
                        {parsedName?.en || 'Unnamed Event'}
                      </td>
                      <td className="p-4 text-gray-400 text-sm">
                        {parsedDate?.zh || parsedDate?.en || '-'}
                      </td>
                      <td className="p-4 text-gray-400 text-sm">
                        {parsedLocation?.zh || parsedLocation?.en || '-'}
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded text-xs border ${event.status === 'published'
                            ? 'bg-green-500/20 text-green-400 border-green-500/50'
                            : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                            }`}
                        >
                          {event.status === 'published' ? '已发布' : '草稿'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded text-sm transition-colors"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => handleTogglePublish(event)}
                            className={`px-3 py-1 rounded text-sm transition-colors ${event.status === 'published'
                              ? 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400'
                              : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                              }`}
                          >
                            {event.status === 'published' ? '下架' : '发布'}
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 编辑器弹窗 */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 overflow-y-auto">
          <div className="bg-[#1a1d17] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* 标题栏 */}
            <div className="flex items-center justify-between p-6 border-b border-[#2a2d27]">
              <h2 className="text-2xl font-bold text-white">
                {currentEvent.id ? '编辑活动' : '创建新活动'}
              </h2>
              <button
                onClick={() => setShowEditor(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            <form
              onSubmit={handleSaveEvent}
              className="p-6 space-y-6 overflow-y-auto"
            >
              {/* 活动ID */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  活动ID {!currentEvent.id && <span className="text-red-400">*</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentEvent.event_id || ''}
                    className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none cursor-not-allowed"
                    placeholder={currentEvent.id ? "系统生成的ID" : "点击按钮自动生成ID"}
                    disabled
                    readOnly
                  />
                  {!currentEvent.id && (
                    <button
                      type="button"
                      onClick={() => {
                        // 生成临时ID: event-时间戳-随机字符
                        const timestamp = Date.now().toString(36);
                        const randomStr = Math.random().toString(36).substring(2, 8);
                        const generatedId = `event-${timestamp}-${randomStr}`;
                        setCurrentEvent({
                          ...currentEvent,
                          event_id: generatedId,
                        });
                      }}
                      className="px-4 py-2 bg-[#f5a623] hover:bg-[#d4900e] text-black font-medium rounded-lg transition-colors whitespace-nowrap"
                    >
                      自动生成ID
                    </button>
                  )}
                </div>
                {!currentEvent.id && !currentEvent.event_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    请先生成活动ID,然后才能填写其他信息
                  </p>
                )}
                {currentEvent.id && (
                  <p className="text-xs text-gray-500 mt-1">
                    编辑时不能修改活动ID
                  </p>
                )}
              </div>

              {/* 表单内容 */}
              <fieldset
                disabled={!currentEvent.event_id}
                className="space-y-6 border-none p-0 m-0"
              >
                {/* 活动名称 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      中文名称
                    </label>
                    <input
                      type="text"
                      value={currentEvent.name?.zh || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          name: { ...currentEvent.name, zh: e.target.value },
                        })
                      }
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      English Name
                    </label>
                    <input
                      type="text"
                      value={currentEvent.name?.en || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          name: { ...currentEvent.name, en: e.target.value },
                        })
                      }
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                </div>

                {/* 活动时间 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      时间（中文）
                    </label>
                    <input
                      type="text"
                      value={currentEvent.date?.zh || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          date: { ...currentEvent.date, zh: e.target.value },
                        })
                      }
                      placeholder="例如: 2024年1月15日"
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Date (English)
                    </label>
                    <input
                      type="text"
                      value={currentEvent.date?.en || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          date: { ...currentEvent.date, en: e.target.value },
                        })
                      }
                      placeholder="e.g., January 15, 2024"
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                </div>

                {/* 活动地点 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      地点（中文）
                    </label>
                    <input
                      type="text"
                      value={currentEvent.location?.zh || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          location: {
                            ...currentEvent.location,
                            zh: e.target.value,
                          },
                        })
                      }
                      placeholder="例如: 北京国际展览中心"
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Location (English)
                    </label>
                    <input
                      type="text"
                      value={currentEvent.location?.en || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          location: {
                            ...currentEvent.location,
                            en: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., Beijing International Exhibition Center"
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                </div>

                {/* 活动简介 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      简介（中文）
                    </label>
                    <textarea
                      value={currentEvent.description?.zh || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          description: {
                            ...currentEvent.description,
                            zh: e.target.value,
                          },
                        })
                      }
                      rows={4}
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Description (English)
                    </label>
                    <textarea
                      value={currentEvent.description?.en || ''}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          description: {
                            ...currentEvent.description,
                            en: e.target.value,
                          },
                        })
                      }
                      rows={4}
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                    />
                  </div>
                </div>

                {/* 活动照片 */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    活动照片
                  </label>
                  <div className="space-y-3">
                    {(currentEvent.images || []).map((image, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="flex-1">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={image || ''}
                              onChange={(e) => {
                                const newImages = [
                                  ...(currentEvent.images || []),
                                ];
                                newImages[index] = e.target.value;
                                setCurrentEvent({
                                  ...currentEvent,
                                  images: newImages,
                                });
                              }}
                              placeholder={`照片 ${index + 1} URL`}
                              className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                            />
                            <label className={`px-4 py-2 ${uploadingImage === index ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                              {uploadingImage === index ? (
                                <>
                                  <svg
                                    className="animate-spin h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  上传中...
                                </>
                              ) : (
                                <>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 256 256"
                                  >
                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                  </svg>
                                  上传
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, index)}
                                className="hidden"
                                disabled={uploadingImage === index}
                              />
                            </label>
                          </div>
                          {/* 图片预览 */}
                          {currentEvent.images?.[index] && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-gray-400 mb-2">
                                预览
                              </label>
                              <div className="relative w-full h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                                <img
                                  src={getImageUrl(currentEvent.images[index])}
                                  alt={`活动照片 ${index + 1} 预览`}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = currentEvent.images.filter(
                              (_, i) => i !== index
                            );
                            setCurrentEvent({
                              ...currentEvent,
                              images: newImages,
                            });
                          }}
                          className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                        >
                          删除
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentEvent({
                          ...currentEvent,
                          images: [...(currentEvent.images || []), ''],
                        });
                      }}
                      className="w-full px-4 py-2 border-2 border-dashed border-[#2a2d27] hover:border-[#4a5f3a] text-gray-400 hover:text-white rounded-lg transition-colors"
                    >
                      + 添加照片
                    </button>
                  </div>
                </div>

              </fieldset>

              {/* 操作按钮 */}
              <div className="flex gap-4 pt-4 border-t border-[#2a2d27]">
                <button
                  type="button"
                  onClick={() => setShowEditor(false)}
                  className="flex-1 px-6 py-3 bg-[#2a2d27] hover:bg-[#3a3d37] text-white rounded-lg font-medium transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {isLoading ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast通知 */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg ${toast.type === 'success'
              ? 'bg-green-500 text-white'
              : toast.type === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-yellow-500 text-white'
              }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      {/* 确认对话框 */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="bg-[#1a1d17] rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">确认操作</h3>
            <p className="text-gray-400 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-4">
              <button
                onClick={confirmDialog.onCancel}
                className="flex-1 px-4 py-2 bg-[#2a2d27] hover:bg-[#3a3d37] text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${confirmDialog.type === 'danger'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white'
                  }`}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
